import { Tab, TabIndicator, TabList, TabPanel, TabPanels, Tabs, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { UserProfile } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $ok } from "foundation-ts/commons";
import { TSError } from "foundation-ts/tserrors";
import { Resp } from "foundation-ts/tsrequest";
import { Nullable } from "foundation-ts/types";

import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { PageShellWithThreeColumns } from "@components/PageShellWithThreeColumns/PageShellWithThreeColumns";
import { AsyncConfirmModal, DefaultAsyncConfirmModalState } from "@/components/Modals/AsyncConfirmModal";
import { WaitingModal } from "@/components/Modals/WaitingModal";
import { CorporationUserInfos } from "@/components/UserInfos/CorporationUserInfos";
import { UserParameters } from "@/components/UserInfos/UserInfos";
import { config } from "@/config/config";
import { AccountTab } from "@containers/userProfile/AccountTab";
import { ConsumptionsTab } from "@containers/userProfile/ConsumptionsTab";
import { SettingsTab } from "@containers/userProfile/SettingsTab";
import { PollingState } from "@/contexts/AuthContext";
import { useAuthContext } from "@/hooks/useAuthContext";
import useQueryParam from "@/hooks/useQueryParams";
import { useSubscription } from "@/hooks/useSubscription";
import { useUsers } from "@/hooks/useUsers";
import { logout } from "@/services/authentication.service";
import { $g1Color } from "@/theme/G1Style";
import { ActionButton, BaseViewProps } from "@/utils/TypesAndConstants";
import { defaultErrorToast, defaultSuccessToast } from "@/utils/toast";

import { SubscriptionTab } from "./SubscriptionTab";
import { UsersTab } from "./UsersTab";

export const UserProfileView = (props: BaseViewProps) => {
    const { lockedAccount } = props;
    const { connectedUser, pollingState, disconnect, startPollingUser, stopPollingUser } = useAuthContext();
    const { addUser, refreshUsers, removeUser, sendActivationLink, setAdminCorporateRole } = useUsers();
    const { data: activeSubscription } = useSubscription();
    const queryCorporationId = useQueryParam("corporationId");
    const mandateState = useQueryParam("mandate");
    const [hiddenSubscriptionTab, setHiddenSubscriptionTab] = useState(true);
    const [hiddenCollaboratorsTab, setHiddenCollaboratorsTab] = useState(true);
    const [showUserInfos, setShowUserInfos] = useState(false);
    const [toAddUser, setToAddUser] = useState(false);
    const [userToShow, setUserToShow] = useState<Nullable<UserParameters>>(null);
    const [confirmModal, setConfirmModal] = useState(DefaultAsyncConfirmModalState);
    const userToShowRef = useRef<Nullable<UserParameters>>(null);
    const { onClose } = useDisclosure();
    const toast = useToast();
    const navigate = useNavigate();

    const corporation = connectedUser?.corporations?.first();
    const maxUsers    = $ok(corporation!.trialPeriod?.extraUsers) ? corporation!.trialPeriod!.extraUsers!+1 : activeSubscription?.maxUsers ?? -1;

    const forceLogout = async () => {
        disconnect();
        await logout();
        navigate(config.app.loginURL!);
    };


    const resetCorporationUserView = () => {
        setToAddUser(false);
        setShowUserInfos(false);
        setUserToShow(null);
    };

    const setUserToShowSync = (user: Nullable<UserParameters>) => {
        userToShowRef.current = user;
        setUserToShow(user);
    };

    const updateUserProfileView = () => {
        setHiddenSubscriptionTab($ok(activeSubscription) && !!activeSubscription!.offline);
        setHiddenCollaboratorsTab(false);
    };

    const handleAddUser = () => {
        setToAddUser(true);
        setShowUserInfos(true);
        setUserToShowSync(null);
    };

    const handleRefreshUsers = () => {
        refreshUsers();
        resetCorporationUserView();
    }

    const handleViewUser = (user?: UserParameters) => {
        setShowUserInfos(true);
        setUserToShowSync(user);
    };

    const onSubmitAddUser = async (user: UserParameters) => {
        addUser.mutate(user, {
            onSuccess: () => { 
                toast(defaultSuccessToast(`Le collaborateur ${user.firstName} ${user.lastName} a été créé - Un lien d'activation lui a été envoyé`));
                resetCorporationUserView();
            },
            onError: async (error: unknown) => { 
                if (error instanceof TSError) {
                    if (error.errorCode === Resp.Unauthorized) {
                        toast(defaultErrorToast(`Erreur lors de l'ajout d'un collaborateur. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.`)) ;
                        await forceLogout();
                    } else if (error.errorCode === Resp.Conflict) {
                        toast(defaultErrorToast(`Vous ne pouvez pas ajouter un nouvel utilisateur à votre organisation parce que vous avez atteint la limite du nombre de collaborateurs autorisés`)) ;
                    } else {
                        toast(defaultErrorToast(error.message));
                    }
                } else {
                    toast(defaultErrorToast("Une erreur inconnue est survenue lors de la création de l'utilisateur"));
                }
            }
        });
    };

    const onSubmitSendActivationLink = async (user: UserParameters) => {
        if (userToShowRef.current?.apid !== user.apid) {
            toast(defaultErrorToast("L'identifiant de l'utilisateur à modifier n'est pas identique à l'identifiant de l'utilisateur sélectionné"));
            return;
        }

        setConfirmModal({
            title: "Confirmation",
            message: `Etes-vous de vouloir ${user.userFlags?.profile === UserProfile.Contact ? "activer l'accès de" : "renvoyer le lien d'activation à"} ${user.firstName} ${user.lastName} ?`,
            onAccept: async () => {
                sendActivationLink.mutate(user, {
                    onSuccess: () => { toast(defaultSuccessToast(`Lien d'activation pour ${user.firstName} ${user.lastName} renvoyé à l'email ${user.email.toString()}`)); },
                    onError: async (error: unknown) => {
                        if (error instanceof TSError) {
                            if (error.errorCode === Resp.Unauthorized) {
                                toast(defaultErrorToast("Erreur lors du renvoi du lien d'activation. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.")) ;
                                await forceLogout();
                            } else {
                                toast(defaultErrorToast(error.message));
                            }
                        } else {
                            toast(defaultErrorToast("Une erreur inconnue est survenue lors du renvoi du lien d'activation"));
                        }
                    }
                });
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            },
            onClose: async () => {
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            }
        }) ;
    };

    const onSubmitDeleteUser = async (user: UserParameters) => {
        if (userToShowRef.current?.apid !== user.apid) {
            toast(defaultErrorToast("L'identifiant de l'utilisateur à supprimer n'est pas identique à l'identifiant de l'utilisateur sélectionné"));
            return;
        }

        setConfirmModal({
            title: "Confirmation",
            message: `Etes-vous sûr de vouloir supprimer définitivement ${user.firstName} ${user.lastName} ?`,
            onAccept: async () => {
                removeUser.mutate(user, {
                    onSuccess: () => { 
                        toast(defaultSuccessToast(`Le compte ${user.userFlags?.activated ? "" : "non activé"} de ${user.firstName} ${user.lastName} a été retiré des effectifs`));
                        resetCorporationUserView();
                    },
                    onError: async (error: unknown) => {
                        if (error instanceof TSError) {
                            if (error.errorCode === Resp.Unauthorized) {
                                toast(defaultErrorToast(`Erreur lors de la suppression définitive de ${user.firstName} ${user.lastName}. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.`));
                                await forceLogout();
                            } else if (error.errorCode === Resp.Conflict) {
                                toast(defaultErrorToast(`L'utilisateur ${user.firstName} ${user.lastName} ne peut pas être supprimé de la plateforme, car il est défini comme contact d'autres utilisateurs et a potentiellement déjà signé des documents.`));
                            } else {
                                toast(defaultErrorToast(`Impossible de supprimer définitivement ${user.firstName} ${user.lastName}`));
                            }
                        } else {
                            toast(defaultErrorToast(`Impossible de supprimer définitivement ${user.firstName} ${user.lastName}`));
                        }
                    }
                });
                setConfirmModal(DefaultAsyncConfirmModalState);
            },
            onClose: async () => {
                setConfirmModal(DefaultAsyncConfirmModalState);
            }
        });
    }

    const onSubmitAddAdminRole = async (user: UserParameters) => {
        if (userToShowRef.current?.apid !== user.apid) {
            toast(defaultErrorToast("L'identifiant de l'utilisateur à modifier n'est pas identique à l'identifiant de l'utilisateur sélectionné"));
            return;
        }

        setConfirmModal({
            title: "Confirmation",
            message: `Etes-vous sûr de vouloir ajouter le rôle de Gestionnaire à ${user.firstName} ${user.lastName} ?`,
            onAccept: async () => {
                setAdminCorporateRole.mutate({userId: user.apid!, toAdd: true}, {
                    onSuccess: () => { 
                        toast(defaultSuccessToast(`Une notification a été envoyée à ${user.firstName} ${user.lastName} afin de le prévenir de son nouveau rôle de Gestionnaire de l'organisation`));
                        resetCorporationUserView();
                    },
                    onError: async (error: unknown) => { 
                        if (error instanceof TSError && error.errorCode === Resp.Unauthorized) {
                            toast(defaultErrorToast(`Erreur lors de l'ajout du rôle Gestionnaire à ${user.firstName} ${user.lastName}. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.`));
                            await forceLogout();
                        } else {
                            toast(defaultErrorToast(`Impossible d'ajouter le rôle de Gestionnaire à ${user.firstName} ${user.lastName}`));
                        }
                    }
                });
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            },
            onClose: async () => {
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            }
        }) ;
    }

    const onSubmitDeleteAdminRole = async (user: UserParameters) => {
        if (userToShowRef.current?.apid !== user.apid) {
            toast(defaultErrorToast("L'identifiant de l'utilisateur à modifier n'est pas identique à l'identifiant de l'utilisateur sélectionné"));
            return;
        }

        setConfirmModal({
            title: "Confirmation",
            message: `Etes-vous sûr de vouloir supprimer le rôle de Gestionnaire à ${user.firstName} ${user.lastName} ?`,
            onAccept: async () => {
                setAdminCorporateRole.mutate({userId: user.apid!, toAdd: false}, {
                    onSuccess: () => { 
                        toast(defaultSuccessToast(`Une notification a été envoyée à ${user.firstName} ${user.lastName} afin de le prevénir qu'il n'est plus Gestionnaire de l'organisation}`));
                        resetCorporationUserView();
                    },
                    onError: async (error: unknown) => { 
                        if (error instanceof TSError && error.errorCode === Resp.Unauthorized) {
                            toast(defaultErrorToast(`Erreur lors de la suppression du rôle Gestionnaire à ${user.firstName} ${user.lastName}. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.`));
                            await forceLogout();
                        } else {
                            toast(defaultErrorToast(`Impossible de supprimer le rôle de Gestionnaire à ${user.firstName} ${user.lastName}`));
                        }
                    }
                });
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            },
            onClose: async () => {
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            }
        }) ;
    };

    const userButtons: ActionButton<UserParameters>[] = [];
    if (!$ok(userToShowRef.current?.userFlags)) {
        userButtons.push({ title: "Créer", variant: "important", onSubmit: onSubmitAddUser });
    } else if (!userToShowRef.current?.userFlags?.activated) {
        userButtons.push({ title: "Renvoyer le lien d'activation", variant: "important", onSubmit: onSubmitSendActivationLink });
        userButtons.push({ title: "Supprimer", variant: "important", onSubmit: onSubmitDeleteUser });
    } else if (connectedUser?.apid !== userToShowRef.current?.apid) { // Only actions for other users
        switch (userToShowRef.current?.userFlags.profile) {
            case UserProfile.UserCorporate:
                userButtons.push({ title: "Définir comme Gestionnaire", variant: "important", onSubmit: onSubmitAddAdminRole }) ;
                userButtons.push({ title: "Retirer des effectifs", variant: "important", onSubmit: onSubmitDeleteUser }) ;
                break ;
                
            case UserProfile.AdministratorCorporate :
                userButtons.push({ title: "Supprimer le rôle Gestionnaire", variant: "important", onSubmit: onSubmitDeleteAdminRole }) ;
                userButtons.push({ title: "Retirer des effectifs", variant: "important", onSubmit: onSubmitDeleteUser }) ;
                break ;

            default:
                break ;
        }
    };

    useEffect(() => {
        if ($ok(queryCorporationId) && corporation?.apid === queryCorporationId && mandateState === "confirmed") {
            startPollingUser();
        }

        return () => stopPollingUser();
    }, []);

    useEffect(() => {
        if (mandateState !== "confirmed") { return; }

        if (pollingState === PollingState.EXHAUSTED) {
            toast(defaultErrorToast("La validation du mandat prend trop de temps, veuillez recommencer"));
            onClose();
            return;
        }

        if (pollingState !== PollingState.STOPPED) { return; }

        const status = corporation?.mandate?.status;
        if (!!corporation?.mandate?.failedToChange) {
            toast(defaultErrorToast("La validation du nouveau mandat a échoué, veuillez recommencer"));
        } else if (status === "valid") {
            toast(defaultSuccessToast("Nous avons pris en compte votre nouveau mandat carte bancaire"));
        } else if (status === "invalid") {
            toast(defaultErrorToast("Votre mandat est invalide, veuillez en définir un nouveau"));
        }
        onClose();
    }, [pollingState]);

    return (
        <PageShellWithThreeColumns
            centralContent={
                <>
                    <ColumnTitle color={$g1Color('global.write')}>
                        <Text>Votre compte</Text>
                    </ColumnTitle>
                    <Tabs isFitted onChange={() => resetCorporationUserView()}>
                        <TabList color={$g1Color('global.tabs.write')} backgroundColor={$g1Color('global.tabs.bg')}>
                            <Tab fontWeight="semibold">Compte</Tab>
                            { !hiddenSubscriptionTab && <Tab fontWeight="semibold">Abonnement</Tab> }
                            { !hiddenCollaboratorsTab && <Tab fontWeight="semibold">Mes collaborateurs</Tab> }
                            <Tab fontWeight="semibold">Mes consommations</Tab>
                            <Tab fontWeight="semibold">Réglages</Tab>
                        </TabList>
                        <TabIndicator color={$g1Color('global.tabs.indicator.write')} backgroundColor={$g1Color('global.tabs.indicator.bg')} />
                        <TabPanels>
                            <TabPanel>
                                <AccountTab updateUserProfileView={updateUserProfileView} lockedAccount={lockedAccount} />
                            </TabPanel>
                            { !hiddenSubscriptionTab &&
                                <TabPanel>
                                    <SubscriptionTab />
                                </TabPanel>
                            }
                            { !hiddenCollaboratorsTab &&
                                <TabPanel>
                                    <UsersTab maxUsers={maxUsers} onAddUser={handleAddUser} onRefreshUsers={handleRefreshUsers} onViewUser={handleViewUser} />
                                </TabPanel>
                            }
                            <TabPanel onClick={() => setShowUserInfos(false)}>
                                <ConsumptionsTab />
                            </TabPanel>
                            <TabPanel onClick={() => setShowUserInfos(false)}>
                                <SettingsTab lockedAccount={lockedAccount} />
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                    {pollingState === PollingState.RUNNING && <WaitingModal isOpen={true} onClose={onClose} message="Le mandat que vous souhaitez mettre à jour est encore en attente de validation" />}
                </>
            }
            rightContent={
                <>
                    <CorporationUserInfos isAdminCorporate isCreation={toAddUser} data={userToShow} visibility={showUserInfos ? "visible" : "hidden"} actionButtons={userButtons} onBack={resetCorporationUserView} />
                    <AsyncConfirmModal title={confirmModal.title} message={confirmModal.message} onAccept={confirmModal.onAccept} onClose={confirmModal.onClose} />
                </>
            }
            isOpen={showUserInfos}
        />
    ) ;
};